import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { costCurveApi } from "@/api/costCurve";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface CostCurveWidgetProps {
  /** YYYY-MM-DD; falls back to today if omitted. */
  date?: string;
}

export function CostCurveWidget({ date }: CostCurveWidgetProps) {
  const { t } = useTranslation();
  const [showChart, setShowChart] = useState(false);

  const cc = useQuery({
    queryKey: ["cost-curve", date ?? "today"],
    queryFn: () => costCurveApi.get(date),
  });

  if (cc.isLoading) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-slate-500">{t("common.loading")}</CardContent>
      </Card>
    );
  }
  if (cc.isError || !cc.data) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-slate-500">
          {t("cost_curve.unavailable")}
        </CardContent>
      </Card>
    );
  }

  const presets = cc.data.presets;
  const powerMax = cc.data.power_max_kw;
  // Пресети йдуть від power_min до power_max; відсоток рахуємо від максимуму,
  // тож при power_min=601 та power_max=1500 вийде 40% / 70% / 100% (а не 50/75/100).
  const presetList = Object.values(presets).sort((a, b) => a.power_kw - b.power_kw);
  const pct = (powerKw: number) =>
    powerMax > 0 ? Math.round((powerKw / powerMax) * 100) : 0;
  const fmt = (n: number) =>
    n.toLocaleString("uk-UA", { maximumFractionDigits: 0 });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{t("cost_curve.title")}</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setShowChart((v) => !v)}>
            {showChart ? t("cost_curve.hide_chart") : t("cost_curve.show_chart")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-3 text-sm">
          {presetList.map((p) => (
            <div key={p.power_kw} className="rounded-lg bg-slate-50 p-3">
              <div className="text-xs text-slate-500">{pct(p.power_kw)}% — {fmt(p.power_kw)} {t("common.kw")}</div>
              <div className="font-mono text-lg">{fmt(p.cost_uah_per_mwh)}</div>
              <div className="text-xs text-slate-500">{t("cost_curve.unit")}</div>
            </div>
          ))}
        </div>

        {showChart && (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={cc.data.points} margin={{ top: 10, right: 20, bottom: 20, left: 50 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="power_kw"
                  type="number"
                  domain={[cc.data.power_min_kw, cc.data.power_max_kw]}
                  tickFormatter={(v) => fmt(v as number)}
                  label={{ value: t("optimizer.axis_power"), position: "insideBottom", offset: -10 }}
                />
                <YAxis
                  tickFormatter={(v) => fmt(v as number)}
                  domain={[
                    (dataMin: number) => Math.floor(dataMin * 0.95),
                    (dataMax: number) => Math.ceil(dataMax * 1.05),
                  ]}
                  width={70}
                />
                <Tooltip formatter={(v) => fmt(v as number)} />
                <Line type="monotone" dataKey="cost_uah_per_mwh" stroke="#2563eb" dot={false} />
                {presetList.map((p) => (
                  <ReferenceLine key={p.power_kw} x={p.power_kw} stroke="#94a3b8" strokeDasharray="3 3" />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
